import { useNavigate } from 'react-router-dom';
import {
  Plus,
  BookOpen,
  AlertTriangle,
  Eye,
  Pencil,
  Trash2,
  X,
  Info,
  ChevronRight,
  SlidersHorizontal,
  Megaphone,
} from 'lucide-react';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import {
  CATEGORIA_CONFIG,
  NIVEL_CONFIG,
  btnSecondary,
} from './observacoes-professor-shared';
import {
  descricaoExibicao,
  formatDataCurta,
  isAvisoPedagogico,
  professorPodeEditarObservacao,
  OBSERVACAO_EDICAO_DIAS_PROFESSOR,
} from './observacoes-utils';
import { ObservacoesFormFields } from './ObservacoesFormFields';
import { ObservacoesAlunoPicker } from './ObservacoesAlunoPicker';
import type { ObservacoesProfessorPageContext } from './useObservacoesProfessorPage';

interface Props {
  ctx: ObservacoesProfessorPageContext;
}

export function ObservacoesMobileView({ ctx }: Props) {
  const navigate = useNavigate();
  const {
    stats,
    showForm,
    setShowForm,
    editingId,
    filtrosAbertos,
    setFiltrosAbertos,
    filtrosAtivos,
    filtroTurma,
    setFiltroTurma,
    filtroCategoria,
    setFiltroCategoria,
    filtroNivel,
    setFiltroNivel,
    minhasTurmas,
    isLoading,
    observacoes,
    selectedId,
    setSelectedId,
    observacaoSelecionada,
    resetForm,
    openEdit,
    handleRemover,
    openNovaObservacao,
  } = ctx;

  return (
    <MobilePageWrapper
      title="Observações"
      icon={BookOpen}
      iconColor="#6ee7b7"
      badge={stats.urgentes > 0 ? `${stats.urgentes} urg.` : undefined}
      badgeColor="#dc2626"
    >
      <div className="admin-page obs-page-root obs-page-mobile">
        <div className="obs-mobile-only">
          <div className="obs-hint-banner">
            <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
            <span>
              <strong>Registro interno</strong> para acompanhamento pedagógico (pode ser privado).
              Para comunicar a família com modelo oficial, use{' '}
              <button
                type="button"
                onClick={() => navigate('/professor/alunos')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#047857',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 'inherit',
                }}
              >
                Meus Alunos → Avisar responsável
              </button>
              .
            </span>
          </div>

          <div className="obs-toolbar">
            <button
              type="button"
              className="obs-btn-nova-compact"
              onClick={() => {
                if (showForm && !editingId) resetForm();
                else openNovaObservacao();
              }}
            >
              <Plus size={16} aria-hidden />
              Nova observação
            </button>
            <button
              type="button"
              className={`obs-filter-toggle ${filtrosAbertos || filtrosAtivos ? 'active' : ''}`}
              onClick={() => setFiltrosAbertos(!filtrosAbertos)}
              aria-label="Filtros"
              aria-expanded={filtrosAbertos}
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>

          <div className="obs-kpi-row-compact">
            <div className="obs-kpi-chip">
              <BookOpen size={14} style={{ color: '#059669' }} aria-hidden />
              <span className="obs-kpi-chip-value">{stats.total}</span>
              <span className="obs-kpi-chip-label">Total</span>
            </div>
            <div className="obs-kpi-chip">
              <AlertTriangle size={14} style={{ color: '#dc2626' }} aria-hidden />
              <span className="obs-kpi-chip-value">{stats.urgentes}</span>
              <span className="obs-kpi-chip-label">Urgentes</span>
            </div>
            <div className="obs-kpi-chip">
              <Eye size={14} style={{ color: '#d97706' }} aria-hidden />
              <span className="obs-kpi-chip-value">{stats.atencao}</span>
              <span className="obs-kpi-chip-label">Atenção</span>
            </div>
          </div>

          {filtrosAbertos && (
            <div className="obs-filtros-panel">
              <div className="obs-filtro-field">
                <label className="obs-filtro-label" htmlFor="obs-filtro-turma">
                  Turma
                </label>
                <select
                  id="obs-filtro-turma"
                  className="obs-filtro-select"
                  value={filtroTurma}
                  onChange={(e) => setFiltroTurma(e.target.value)}
                >
                  <option value="">Todas as turmas</option>
                  {minhasTurmas.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="obs-filtro-field">
                <label className="obs-filtro-label" htmlFor="obs-filtro-cat">
                  Categoria
                </label>
                <select
                  id="obs-filtro-cat"
                  className="obs-filtro-select"
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value as typeof filtroCategoria)}
                >
                  <option value="">Todas</option>
                  {Object.entries(CATEGORIA_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="obs-filtro-field">
                <label className="obs-filtro-label" htmlFor="obs-filtro-nivel">
                  Nivel
                </label>
                <select
                  id="obs-filtro-nivel"
                  className="obs-filtro-select"
                  value={filtroNivel}
                  onChange={(e) => setFiltroNivel(e.target.value as typeof filtroNivel)}
                >
                  <option value="">Todos</option>
                  {Object.entries(NIVEL_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {showForm && (
            <div className="obs-form-sheet">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>
                  {editingId ? 'Editar observação' : 'Nova observação'}
                </h3>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  <X size={18} />
                </button>
              </div>
              <ObservacoesFormFields {...ctx} />
            </div>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              Carregando...
            </div>
          ) : observacoes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
              <BookOpen size={36} style={{ opacity: 0.25, marginBottom: 8 }} />
              <p style={{ fontSize: '0.82rem', margin: 0 }}>Nenhuma observação encontrada</p>
            </div>
          ) : (
            <div className="obs-list-compact">
              {observacoes.map((obs) => {
                const catInfo = CATEGORIA_CONFIG[obs.categoria];
                const nivelInfo = NIVEL_CONFIG[obs.nivelAtencao];
                const aviso = isAvisoPedagogico(obs);
                const desc = descricaoExibicao(obs.descricao || '');
                return (
                  <div key={obs.id} className="obs-item">
                    <button
                      type="button"
                      className="obs-item--tap"
                      onClick={() => setSelectedId(obs.id)}
                    >
                      <div
                        className="obs-item-icon"
                        style={{ background: catInfo.bg, color: catInfo.color }}
                      >
                        {aviso ? <Megaphone size={14} /> : catInfo.icon}
                      </div>
                      <div className="obs-item-body">
                        <div className="obs-item-title-row">
                          <span className="obs-item-title">{obs.titulo}</span>
                          {aviso && (
                            <span
                              className="obs-item-badge"
                              style={{ background: '#dbeafe', color: '#1d4ed8' }}
                            >
                              Aviso
                            </span>
                          )}
                          <span
                            className="obs-item-badge"
                            style={{ background: catInfo.bg, color: catInfo.color }}
                          >
                            {catInfo.label}
                          </span>
                        </div>
                        <p className="obs-item-meta">
                          {obs.aluno?.nome || '—'} · {formatDataCurta(obs.criadoEm)}
                          {desc ? ` · ${desc}` : ''}
                        </p>
                      </div>
                      <div className="obs-item-aside">
                        <span
                          className="obs-item-nivel"
                          style={{ background: nivelInfo.bg, color: nivelInfo.color }}
                        >
                          {nivelInfo.label}
                        </span>
                        <ChevronRight size={16} className="obs-item-chevron" aria-hidden />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {observacaoSelecionada && selectedId && (
          <div
            className="obs-detalhe-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedId(null);
            }}
            role="presentation"
          >
            <div className="obs-detalhe-sheet" onClick={(e) => e.stopPropagation()} role="dialog">
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.15)',
                  margin: '0 auto 10px',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h2 style={{ fontSize: '0.95rem', fontWeight: 800, margin: 0, flex: 1, paddingRight: 8 }}>
                  {observacaoSelecionada.titulo}
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                >
                  <X size={18} />
                </button>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                {observacaoSelecionada.aluno?.nome} · {formatDataCurta(observacaoSelecionada.criadoEm)}
              </p>
              <p style={{ fontSize: '0.82rem', lineHeight: 1.5, margin: 0, color: 'var(--text-main)' }}>
                {descricaoExibicao(observacaoSelecionada.descricao || '')}
              </p>
              {isAvisoPedagogico(observacaoSelecionada) ? (
                <p style={{ fontSize: '0.68rem', color: '#1d4ed8', marginTop: 12, lineHeight: 1.4 }}>
                  Registro gerado pelo fluxo de aviso ao responsavel. Somente leitura.
                </p>
              ) : professorPodeEditarObservacao(observacaoSelecionada) ? (
                <div className="obs-detalhe-actions">
                  <button type="button" onClick={() => openEdit(observacaoSelecionada)} style={btnSecondary}>
                    <Pencil size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemover(observacaoSelecionada.id)}
                    style={{ ...btnSecondary, color: '#dc2626', borderColor: '#fecaca' }}
                  >
                    <Trash2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Remover
                  </button>
                </div>
              ) : (
                <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 12 }}>
                  Registro antigo ({'>'} {OBSERVACAO_EDICAO_DIAS_PROFESSOR} dias). Somente leitura — coordenacao
                  pode alterar.
                </p>
              )}
            </div>
          </div>
        )}

        <ObservacoesAlunoPicker {...ctx} variant="sheet" />
      </div>
    </MobilePageWrapper>
  );
}
